// Die Verdrahtung: Sitzung, Phaser-Spiel, Oberflaeche, Zuege.
//
// Der einzige Ort, an dem alles zusammenkommt — und deshalb der einzige, der
// vom Schichten-Contract ausgenommen ist (Gate FG-04). Das Analogon zu
// `config/` im Django-Backend.
//
// Hier liegt auch die **eine** Fallunterscheidung zwischen Solo und Online: ein
// Zug geht entweder an den Server oder in den lokalen Spielstand. Ueberall
// sonst ist der Unterschied unsichtbar.

import Phaser from 'phaser';

import { bus } from './core/bus';
import { escapeHtml } from './core/format';
import {
  balancing, build as buildById, planetById, recipe as recipeById, story,
} from './data/balancing';
import { isMuted, setMuted, sfx, unlockAudio } from './audio/synth';
import { Session } from './net/session';
import { applyLocalMove, applyRemoteMove, applyRemoteWipe, type MoveKind } from './net/moves';
import { colonyId } from './net/colony';
import { BootScene } from './scenes/BootScene';
import { JumpScene } from './scenes/JumpScene';
import { LandingScene } from './scenes/LandingScene';
import { MapScene } from './scenes/MapScene';
import { PlanetScene } from './scenes/PlanetScene';
import { SpaceScene } from './scenes/SpaceScene';
import { TechTreeScene } from './scenes/TechTreeScene';
import { sideClaimableCount } from './sim/sidequests';
import { builtIds } from './sim/stats';
import { hintSeen, markHintSeen, saveLocal, saveMuted, store, wipeLocal } from './state/store';
import { Account } from './ui/account';
import { landingHtml } from './ui/colony';
import { ColonyPanel } from './ui/colonypanel';
import { Hud } from './ui/hud';
import { PANEL_KEYS } from './ui/keys';
import { moveFeedback } from './ui/movetoast';
import { Panels } from './ui/panels';

import './style.css';

/**
 * Die Marke, dass die Heimflug-Sequenz schon lief.
 *
 * Sie liegt bei den Oberflächen-Hinweisen, nicht im Spielstand: „schon gesehen"
 * ist kein Fortschritt, sondern eine Eigenschaft dieses Browsers (Gate FG-28).
 */
const JUMP_HINT = 'jump-sequence';

const params = new URLSearchParams(globalThis.location?.search ?? '');
const session = new Session({
  baseUrl: import.meta.env.VITE_API_URL ?? globalThis.location.origin,
  forceSolo: params.get('solo') === '1',
});

const game = new Phaser.Game({
  type: Phaser.AUTO,
  parent: 'game',
  backgroundColor: '#0e3a22',
  // Der Pixel-Look ist ein Vertrag: kein Glaetten, ganzzahlige Positionen.
  pixelArt: true,
  roundPixels: true,
  scale: {
    mode: Phaser.Scale.RESIZE,
    width: '100%',
    height: '100%',
  },
  scene: [BootScene, SpaceScene, TechTreeScene, MapScene, PlanetScene, JumpScene, LandingScene],
});

game.scene.start(BootScene.KEY, { session });

// ── Oberflaeche ──────────────────────────────────────────────────────────────
const spaceScene = (): SpaceScene | undefined => game.scene.getScene(SpaceScene.KEY) as SpaceScene | undefined;

/**
 * Die Weltszene, sobald sie **läuft** — für alles, was ihren Zustand liest.
 *
 * `getScene` liefert die Instanz, sobald sie **registriert** ist, also lange
 * bevor `create()` ihre Felder angelegt hat. In dieser Lücke ist `?.` eine
 * Attrappe: die Szene ist da, ihr Schiff nicht, und `warpState()` liest
 * `undefined.warping`. Genau das passierte beim ersten Bild eines kalten
 * Starts — bei einem neuen Spieler zuverlässig, weil ohne Cache jedes Bild
 * länger braucht als bei dem, der schon einmal da war.
 *
 * Der Riegel ist derselbe wie in `syncTree` und `syncMap`: `isActive()` ist
 * genau dann wahr, wenn `create()` durch ist. Er steht hier und nicht in der
 * Szene, weil die Lücke der **Verdrahtung** gehört: die Szene ist nicht
 * kaputt, sie ist noch nicht fertig.
 *
 * Lebenszyklus (`pause`, `resume`, `start`) fragt weiter `spaceScene()` —
 * eine Szene anzuhalten, die noch entsteht, ist kein Zustandszugriff.
 */
const liveSpaceScene = (): SpaceScene | undefined => {
  const scene = spaceScene();
  return scene?.scene.isActive() ? scene : undefined;
};
const treeScene = (): TechTreeScene | undefined =>
  game.scene.getScene(TechTreeScene.KEY) as TechTreeScene | undefined;
const mapScene = (): MapScene | undefined => game.scene.getScene(MapScene.KEY) as MapScene | undefined;
const planetScene = (): PlanetScene | undefined =>
  game.scene.getScene(PlanetScene.KEY) as PlanetScene | undefined;

const panels = new Panels({
  buyNode: (id) => void applyMove('node', id),
  build: (id) => void applyMove('build', id),
  claimQuest: (id) => void applyMove('quest', id),
  claimSide: (id) => void applyMove('side', id),
  // Der Kurs gehört der Weltszene: `ui` und `scenes` kennen sich nicht
  // (Gate FG-04), und die Weltsaat liegt dort. Das Panel bittet, sie antwortet.
  setCourse: (id) => spaceScene()?.courseToSideStep(id),
  buyBooster: (id) => void applyMove('booster', id),
  boosterMode: (on) => void applyMove('boostermode', on ? 'on' : ''),
  spendPoint: (id) => void applyMove('perk', id),
  convert: (id, times) => void applyMove('convert', id, times),
  toggleOre: (id) => void applyMove('ore', id),
  setWishOre: (id) => void applyMove('wish', id),
  homeJump: () => void applyMove('home', ''),
  // Das `catch` liegt im Netzmodul, nicht hier (Gate FG-13, `netzwerk.md`):
  // `null` heisst „nicht erreichbar", und der Reiter sagt es.
  ranking: (board, span) => session.api.ranking(board, span),
  wipe: () => void wipe(),
  setPaused: (paused) => {
    const scene = spaceScene();
    if (!scene) return;
    if (paused) scene.scene.pause();
    else scene.scene.resume();
  },
  treeViewport: () => document.getElementById('tree-viewport') as HTMLElement,
  mapViewport: () => document.getElementById('map-viewport') as HTMLElement,
});

const account = new Account(session, panels);

// Die Kolonie (Akt III). Das Panel kennt keine Szene und keine Züge: es bittet,
// die Verdrahtung macht daraus einen Zug — dieselbe Weiche wie jeder andere
// (`net/moves.ts`) und dieselbe Kodierung an einer Stelle (`net/colony.ts`).
const colony = new ColonyPanel({
  place: (planet, kind, x, y) => void applyMove('place', colonyId.place(planet, kind, x, y)),
  upgrade: (planet, x, y, times) => void applyMove('upgrade', colonyId.tile(planet, x, y), times),
  recipe: (planet, x, y, recipe) => void applyMove('recipe', colonyId.recipe(planet, x, y, recipe)),
  switchOn: (planet, x, y, on) => void applyMove('switch', colonyId.switch(planet, x, y, on)),
  relocate: (planet, x, y, tx, ty) => void applyMove('relocate', colonyId.relocate(planet, x, y, tx, ty)),
  project: (planet) => void applyMove('project', colonyId.planet(planet)),
  land: (planet) => askLanding(planet),
  course: (planet) => {
    spaceScene()?.courseToPlanet(planet);
    panels.close();
  },
});

const hud = new Hud({
  distToBase: () => currentDist(),
  shipX: () => currentShip()?.x ?? 0,
  shipY: () => currentShip()?.y ?? 0,
  docked: () => currentDist() < balancing().dockRange,
  claimable: () => countClaimable(),
  buildable: () => canBuildNext(),
  anomaly: () => currentAnomaly(),
  course: () => liveSpaceScene()?.activeCourse() ?? null,
  warp: () => liveSpaceScene()?.warpState() ?? null,
  orbit: () => {
    const planet = liveSpaceScene()?.orbitPlanet();
    return planet ? { name: planet.name, founded: Boolean(store.save.colony.planets[planet.id]) } : null;
  },
});

/**
 * Das Schiff der laufenden Szene — oder `undefined`, solange es keines gibt.
 *
 * `getScene` liefert die Szene, sobald sie **registriert** ist, lange bevor
 * `create()` ihre Felder angelegt hat; `liveSpaceScene` prüft `isActive()` und
 * hält genau diese Lücke ab (`oberflaeche.md`).
 */
function currentShip(): { x: number; y: number; distToBase: number } | undefined {
  const scene = liveSpaceScene() as unknown as {
    ship?: { x: number; y: number; distToBase: number };
  } | undefined;
  return scene?.ship;
}

function currentDist(): number {
  return currentShip()?.distToBase ?? 0;
}

function currentAnomaly(): string | null {
  const scene = liveSpaceScene() as unknown as { activeAnomaly?: () => string | null } | undefined;
  return scene?.activeAnomaly?.() ?? null;
}

function countClaimable(): number {
  let count = 0;
  for (const quest of balancing().quests) {
    if (store.save.quests[quest.id]) continue;
    const goal = quest.goal;
    const progress =
      goal.type === 'rocks' ? store.stat('rocks')
        : goal.type === 'res' ? store.save.totalRes[goal.id ?? ''] ?? 0
          : goal.type === 'level' ? store.save.ship.level
            : goal.type === 'build' ? store.save.built
              : goal.type === 'depth' ? Math.round(store.stat('deepest'))
                : store.upgradeTotal;
    if (progress >= goal.n) count++;
  }
  // Die Nebenaufträge zählen mit: für den Spieler ist „etwas abzuholen" eine
  // Frage, keine zwei.
  return count + sideClaimableCount(store.save, builtIds(store.save.built));
}

/**
 * Der Karten-Reiter erscheint erst mit dem Kartenwerk.
 *
 * Wie der Booster-Reiter: ein Reiter, der nichts zeigt, ist ein Versprechen
 * ohne Einlösung — und ein gesperrter Reiter wäre ein Klick, der nichts tut.
 */
function syncMapTab(): void {
  if (!store.stats.unlocks.map) return;
  document.querySelector('.tab[data-tab="map"]')?.classList.remove('hidden');
}

/** Der Kolonie-Reiter erscheint mit dem Heimatfeuer — wie die Karte mit dem Kartenwerk. */
function syncColonyTab(): void {
  if (!ColonyPanel.visible()) return;
  document.querySelector('.tab[data-tab="colony"]')?.classList.remove('hidden');
}

function canBuildNext(): boolean {
  const next = balancing().builds[store.save.built];
  return Boolean(next) && store.canAfford(next.cost);
}

// ── Zuege ────────────────────────────────────────────────────────────────────
/**
 * Ein Zug. Wo er ausgefuehrt wird, entscheidet `net/moves`; hier steht nur,
 * was der Spieler davon **hoert**.
 *
 * Der Rueckgabewert sagt, ob der Zug durchging. Alle Aufrufer verwerfen ihn mit
 * `void`; nur der Technologiebaum liest ihn, um sein Halten zu beenden.
 */
async function applyMove(kind: MoveKind, id: string, times = 1): Promise<boolean> {
  // Wie viel dabei entsteht, sagt keine Antwort: der Server schickt den ganzen
  // Spielstand, und die Menge wird geklemmt (`PROGRESS-001`). Gemessen wird
  // deshalb der **Zuwachs** — und das ist sicher, nicht geraten: ein
  // Konverterprodukt liegt in keiner Zone, in keiner Aera (ein Gate prueft es),
  // kann also aus nichts anderem stammen. Ein zweiter gleichzeitiger Zug ist
  // durch die Ein-Zug-Sperre in `net/moves.ts` ausgeschlossen.
  const ausgang = kind === 'convert' ? recipeById(id)?.outRes : undefined;
  const vorher = ausgang ? (store.save.res[ausgang] ?? 0) : 0;

  const result = session.mode === 'online'
    ? await applyRemoteMove(session.api, kind, id, times)
    : applyLocalMove(kind, id, times);

  if (!result.ok) {
    if (result.message) bus.emit('toast', { text: result.message, tone: '' });
    return false;
  }
  if (session.mode === 'solo') saveLocal();
  afterMove(kind, id, ausgang ? (store.save.res[ausgang] ?? 0) - vorher : 0);
  return true;
}

function afterMove(kind: MoveKind, id: string, gewonnen = 0): void {
  bus.emit('state:changed', undefined);
  bus.emit('tree:refresh', undefined);

  // Meldung und Klang stehen als **vollstaendige** Tabelle in
  // `ui/movetoast.ts`. Vorher war das eine Kette von `if`-Zweigen mit einem
  // Schlusszweig ohne Bedingung, und der lautete „<Booster> aktiviert!": vier
  // Zugarten fielen hindurch und logen dem Spieler etwas vor, ohne dass etwas
  // scheiterte (Issue #79). Jetzt faellt niemand mehr durch — eine neue Zugart
  // ohne Zeile in der Tabelle ist ein Typfehler.
  const feedback = moveFeedback(kind, id, store.save, gewonnen);
  for (const sound of feedback.sounds) sfx[sound]();
  if (feedback.toast) bus.emit('toast', feedback.toast);

  // Was darueber hinaus geschieht. Nichts davon ist Text, und deshalb steht es
  // hier und nicht in der Tabelle: es braucht Szene, Panel und Konto.
  if (kind === 'node') {
    // Jetzt hat der Spieler etwas zu verlieren — jetzt ist der Hinweis kein
    // Vorab-Formular, sondern eine Antwort auf eine Frage, die er sich stellt.
    account.hintIfGuest();
  } else if (kind === 'build') {
    raiseBuild(id);
  } else if (kind === 'land') {
    // Wie der Heimflug: die Kapsel steht schon im Spielstand, die Sequenz
    // zeigt sie nur — und wer sie überspringt, verliert nichts.
    startLanding(id);
  } else if (kind === 'home') {
    // Die Aera steht bereits im Spielstand — die Sequenz **zeigt** nur, was
    // schon gilt (`VIEW-001`). Sie entscheidet nichts, und wer sie ueberspringt,
    // verliert nichts.
    startJumpSequence();
  }
}

/**
 * Das neue Bauteil an der Kapsel zeigen und das Raetsel anbieten.
 *
 * Das Bauteil wird **vor** dem Raetsel scharf gestellt und laeuft trotzdem erst
 * danach: das Modal haelt die Szene an, und damit steht `elapsed`. Wer das
 * Raetsel schliesst, sieht den Aufbau — statt ihn hinter einem Dialog zu
 * verpassen. Und das Raetsel entscheidet nichts (siehe `ui/puzzle.ts`).
 */
function raiseBuild(id: string): void {
  const target = buildById(id);
  if (!target) return;
  spaceScene()?.raiseModule(target.id);
  panels.showPuzzle(store.save.built, target.name, balancing().world.defaultSeed);
}

/**
 * Die Heimflug-Sequenz starten — und sie **einmal** merken.
 *
 * „Nach dem Sprung und beim ersten Betreten von Akt II, danach nie wieder
 * ungefragt": die Marke dafuer ist eine Eigenschaft dieses Browsers, kein
 * Fortschritt. Sie gehoert deshalb neben die Ton-Einstellung und **nicht** in
 * den Spielstand (Gate FG-28).
 */
function startJumpSequence(): void {
  markHintSeen(JUMP_HINT);
  if (game.scene.isActive(JumpScene.KEY)) return;
  // Wie ein Dialog haelt sie die Welt an: eine weiterlaufende Simulation
  // hinter einer Sequenz verbraucht Energie, waehrend niemand zusieht.
  spaceScene()?.scene.pause();
  game.scene.start(JumpScene.KEY);
}

// Beide Ausgaenge der Sequenz — Ablauf und Ueberspringen — treffen sich hier.
bus.on('jump:done', () => {
  spaceScene()?.scene.resume();
  // **Erst danach** die Ära übernehmen: `enterEra` baut das Feld neu auf, und
  // eine pausierte Szene nähme den Aufbau entgegen, ohne ihn zu zeichnen.
  spaceScene()?.enterEra();
  bus.emit('state:changed', undefined);
});

/**
 * Vor der Landung den Preis zeigen — **bevor** gezahlt wird.
 *
 * Die Landung nimmt Vorräte aus dem Laderaum; ein Knopf, der das ohne Frage
 * tut, machte aus dem Kauf ein Ereignis (`GAME-001`). Der Text kommt aus
 * `ui/colony.ts`, die Sperre aus derselben Auswertung wie dort.
 */
function askLanding(planetId: string): void {
  const planet = planetById(planetId);
  if (!planet) return;
  panels.showModal(`Landung auf ${planet.name}`, landingHtml(store.save, planet), [
    { label: 'Landen', fn: () => void applyMove('land', colonyId.planet(planetId)) },
    { label: 'Abbrechen', dim: true },
  ]);
}

/** Die Landesequenz starten. Sie hält die Welt an, wie ein Dialog. */
function startLanding(planetId: string): void {
  colony.focus(planetId);
  colony.resetKeys();
  if (game.scene.isActive(LandingScene.KEY)) return;
  panels.close();
  spaceScene()?.scene.pause();
  game.scene.start(LandingScene.KEY, { planet: planetId });
}

// Beide Ausgänge der Landesequenz treffen sich hier — und danach steht der
// Spieler vor seiner Kapsel, nicht wieder im Feld.
bus.on('landing:done', () => {
  spaceScene()?.scene.resume();
  panels.show('colony');
  bus.emit('state:changed', undefined);
});

async function wipe(): Promise<void> {
  if (session.mode === 'online') {
    const result = await applyRemoteWipe(session.api);
    if (!result.ok) {
      bus.emit('toast', { text: result.message ?? 'Fehlgeschlagen', tone: '' });
      return;
    }
  } else {
    wipeLocal();
  }
  spaceScene()?.hardReset();
  bus.emit('toast', { text: 'Neuer Spielstand', tone: '' });
  bus.emit('tree:refresh', undefined);
}

// ── Tastatur ─────────────────────────────────────────────────────────────────
// Die Belegung steht **einmal**, in `ui/keys.ts`, und Gate FG-39 haelt sie mit
// der Tabelle im README zusammen. Hier wird sie nur noch gelesen.

addEventListener('keydown', (event) => {
  unlockAudio();
  const key = event.key.toLowerCase();
  if (PANEL_KEYS[key]) {
    event.preventDefault();
    panels.toggle(PANEL_KEYS[key]);
  } else if (key === 'm') {
    event.preventDefault();
    toggleMute();
  } else if (key === 'escape') {
    if (panels.modalOpen) panels.hideModal();
    // Im Kolonie-Reiter legt `Esc` zuerst den Bauplan weg — erst danach
    // schließt es das Panel. Sonst verlöre man mit dem Plan das ganze Panel.
    else if (panels.open && panels.tab === 'colony' && colony.cancel()) return;
    else if (panels.open) panels.close();
  }
});

document.getElementById('btn-mute')?.addEventListener('click', () => toggleMute());

function toggleMute(): void {
  const next = !isMuted();
  setMuted(next);
  saveMuted(next);
  const button = document.getElementById('btn-mute');
  if (button) button.textContent = next ? '♪̸' : '♪';
}

// ── Der Takt der Oberflaeche ─────────────────────────────────────────────────
// Bewusst ein eigener Lauf: das HUD ist DOM und haengt nicht an der Szene.
// Der Technologiebaum bekommt seinen Ausschnitt aus dem Panel — so bleibt die
// Zweiteilung erhalten, ohne dass `ui` und `scenes` sich kennen (Gate FG-04).
// Der Takt bewahrt sich selbst. Ohne das `finally` stand das naechste Bild
// **hinter** dem Wurf: ein einziger Fehler in einem der sechs Aufrufe brach die
// Kette, und HUD, Panels, Kontochip und die beiden Szenen-Ausschnitte standen
// fuer den Rest der Sitzung still. Genau das war der teure Teil des
// Startfehlers oben — sichtbar als „die Hilfe wechselt den Reiter nicht" und
// „das Upgrade-Panel ist durchsichtig", zwei Symptome ohne erkennbare
// gemeinsame Ursache, weil keins davon nach einem Fehler aussieht.
//
// Gefangen wird **nichts**: der Fehler steht weiter in der Konsole, laut wie
// gefordert (FG-13). Nur die Schleife ueberlebt ihn.
function uiTick(): void {
  try {
    hud.update();
    syncMapTab();
    syncColonyTab();
    panels.render();
    account.render();
    syncTree();
    syncMap();
    syncColony();
  } finally {
    requestAnimationFrame(uiTick);
  }
}

/** Merkt sich, worauf der Baum zuletzt eingepasst wurde. */
let treeFit = '';

function syncTree(): void {
  const scene = treeScene();
  const viewport = document.getElementById('tree-viewport');
  if (!scene || !viewport || !scene.scene.isActive()) return;

  const active = panels.open && panels.tab === 'upgrades';
  scene.scene.setVisible(active);
  // Sichtbarkeit allein reicht nicht: die Szene laeuft weiter, ihre Zeiger
  // liegen weiter auf dem Canvas, und ihre Kamera steht noch auf dem Ausschnitt
  // des geschlossenen Panels. Ohne diese Zeile zeigte der Baum seinen Tooltip
  // ueber der Welt — und ein Klick dorthin kaufte sogar.
  scene.setOpen(active);
  if (!active) {
    treeFit = '';
    return;
  }
  scene.setCallbacks({
    // Eine Ablehnung beendet das Halten: sonst wiederholte der Baum den
    // aussichtslosen Zug, solange der Finger liegen bleibt.
    onBuy: (id) => void applyMove('node', id).then((ok) => { if (!ok) scene.stopHold(); }),
    onHover: (node, x, y) => panels.showNodeTooltip(node, x, y),
  });

  const rect = viewport.getBoundingClientRect();
  scene.setViewportRect(rect);

  // Beim Oeffnen und nach einem Groessenwechsel (Drehen des Geraets) neu
  // einpassen — sonst zeigt der Ausschnitt eine leere Stelle neben dem Baum,
  // und auf dem Handy gibt es keinen Weg zurueck ausser blindem Ziehen.
  const fit = `${Math.round(rect.width)}x${Math.round(rect.height)}`;
  if (fit !== treeFit) {
    treeFit = fit;
    scene.resetView();
  }
}

/**
 * Die Karte an ihren Panelbereich binden — dieselbe Bewegung wie beim Baum.
 *
 * Und derselbe zweite Riegel: `setVisible` schaltet nur das Zeichnen ab. Ohne
 * `setOpen` nähme die Szene weiter Zeiger entgegen, und ein Klick auf die Welt
 * setzte einen Kurs, den niemand gesehen hat.
 */
function syncMap(): void {
  const scene = mapScene();
  const viewport = document.getElementById('map-viewport');
  if (!scene || !viewport || !scene.scene.isActive()) return;

  const active = panels.open && panels.tab === 'map';
  scene.scene.setVisible(active);
  scene.setOpen(active);
  if (!active) return;

  const ship = liveSpaceScene() as unknown as { ship?: { x: number; y: number } } | undefined;
  scene.setWorld(
    balancing().world.defaultSeed,
    ship?.ship?.x ?? 0, ship?.ship?.y ?? 0,
    liveSpaceScene()?.activeCourse() ?? null,
  );
  scene.setCallbacks({
    onPick: (mark) => {
      if (mark.kind === 'side' && mark.id) spaceScene()?.courseToSideStep(mark.id);
      else spaceScene()?.setCourse(mark.x, mark.y, mark.label);
    },
    onPickPoint: (x, y) => spaceScene()?.setCourse(x, y, 'Punkt auf der Karte'),
  });
  scene.setViewportRect(viewport.getBoundingClientRect());
}

/**
 * Das Kolonie-Panel und seine Planetenszene — dieselbe Bewegung wie bei der Karte.
 *
 * Und derselbe zweite Riegel: `setVisible` schaltet nur das Zeichnen ab. Ohne
 * `setOpen` setzte ein Tipp auf die Welt eine Anlage, die niemand gesehen hat.
 */
function syncColony(): void {
  const scene = planetScene();
  const viewport = document.getElementById('colony-viewport');
  if (!scene || !viewport || !scene.scene.isActive()) return;

  const active = panels.open && panels.tab === 'colony';
  scene.scene.setVisible(active);
  scene.setOpen(active);
  if (!active) return;

  const now = performance.now() / 1000;
  colony.render(now);
  scene.setView(colony.view, colony.currentFlow(now).starved);
  scene.setCallbacks({ onTile: (x, y) => colony.tapTile(x, y) });
  scene.setViewportRect(viewport.getBoundingClientRect());
}

requestAnimationFrame(uiTick);

// ── Die Ausgangslage ─────────────────────────────────────────────────────────
// Einmal, beim allerersten Start. Dass er gesehen wurde, merkt sich der
// **Browser** (`markHintSeen`) — nicht der Spielstand: ein Oberflaechen-Hinweis
// ist kein Fortschritt und braeuchte sonst einen zweiten Schreibpfad (FG-28).
if (!hintSeen('intro')) {
  const auftakt = story()[0];
  panels.showModal(
    auftakt?.title ?? 'Sektor R17G',
    `<p style="line-height:1.6">${escapeHtml(auftakt?.text ?? '')}</p>
     <p style="margin-top:10px;line-height:1.6">Dein Laser feuert von selbst auf alles in
     Reichweite. Flieg raus, bau ab, komm zur Kapsel zurück — und rüste auf, bis der
     <b>Teilchenbeschleuniger</b> steht.</p>`,
    [{ label: 'Los geht\u2019s' }],
  );
  markHintSeen('intro');
}

// Und beim **ersten Betreten** von Akt II — etwa auf einem zweiten Gerät, oder
// nachdem der Sprung in einer anderen Sitzung gebucht wurde. Danach nie wieder
// ungefragt: die Marke ist eine Eigenschaft dieses Browsers.
if (store.save.era === balancing().eraHome && !hintSeen(JUMP_HINT)) startJumpSequence();

addEventListener('beforeunload', () => { if (store.authoritative) saveLocal(); });

bus.on('state:changed', () => bus.emit('tree:refresh', undefined));
